(() => {
  if (customElements.get('pu-space-viewer')) return;

  const scriptPromises = new Map();

  class ProjectUnitySpaceViewer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.dialog = this.querySelector('[data-pu-space-viewer-dialog]');
      this.media = this.querySelector('[data-pu-space-viewer-media]');
      this.titleNode = this.querySelector('[data-pu-space-viewer-title]');
      this.gestureHint = this.querySelector('[data-pu-space-viewer-gesture]');
      this.spaceAction = this.querySelector('[data-pu-space-viewer-space-action]');
      this.cameraButton = this.querySelector('[data-pu-space-viewer-camera]');
      this.statusRow = this.querySelector('[data-pu-space-viewer-status-row]');
      this.status = this.querySelector('[data-pu-space-viewer-status]');
      this.retryButton = this.querySelector('[data-pu-space-viewer-retry]');
      this.handoff = this.querySelector('[data-pu-space-viewer-handoff]');
      this.handoffLink = this.querySelector('[data-pu-space-viewer-handoff-link]');
      this.qrNode = this.querySelector('[data-pu-space-viewer-qr]');
      this.qrStatus = this.querySelector('[data-pu-space-viewer-qr-status]');
      this.qrRetry = this.querySelector('[data-pu-space-viewer-qr-retry]');
      this.config = this.readConfig();
      this.config.products = this.config.products
        .map((item) => ({ ...item, glb: this.normalizeImageUrl(item?.glb), usdz: this.normalizeImageUrl(item?.usdz) }))
        .filter((item) => item?.handle && this.isAssetUrl(item.glb) && this.isAssetUrl(item.usdz))
        .map((item) => ({ ...item, hero: this.normalizeImageUrl(item.hero), poster: this.normalizeImageUrl(item.poster), scaleLocked: true }));
      this.products = new Map(this.config.products.map((item) => [item.handle, item]));
      const requestedHandle = new URL(window.location.href).searchParams.get('space_product');
      this.selectedHandle = this.products.has(requestedHandle) ? requestedHandle
        : this.products.has(this.dataset.selectedProduct) ? this.dataset.selectedProduct
          : this.config.products[0]?.handle;
      this.mode = 'space';
      this.renderToken = 0;
      this.modelSourceOverrides = new Map();
      this.failedModelSources = new Set();
      this.modelRetryAttempt = 0;
      this.opener = null;
      this.lockedViewport = null;
      this.pendingViewport = null;
      this.mobileDevice = this.isMobileDevice();
      this.classList.toggle('is-mobile-device', this.mobileDevice);

      this.querySelectorAll('[data-pu-space-viewer-mode]').forEach((button) => {
        button.addEventListener('click', () => this.setMode(button.dataset.puSpaceViewerMode));
        button.addEventListener('focus', () => this.revealModeControl(button));
      });
      this.querySelectorAll('[data-pu-space-viewer-option]').forEach((button) => {
        button.hidden = !this.products.has(button.dataset.puSpaceViewerOption);
        button.addEventListener('click', () => this.select(button.dataset.puSpaceViewerOption, true));
        button.addEventListener('focus', () => this.revealChoice(button));
      });
      this.querySelector('[data-pu-space-viewer-close]')?.addEventListener('click', () => this.close());
      this.cameraButton?.addEventListener('click', () => this.openCamera());
      this.retryButton?.addEventListener('click', () => this.retryCurrentView());
      this.qrRetry?.addEventListener('click', () => {
        const product = this.products.get(this.selectedHandle);
        if (product && this.isActive()) this.renderQr(product, this.renderToken);
      });
      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog?.addEventListener('keydown', (event) => this.handleDialogKeydown(event));
      this.dialog?.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right
          || event.clientY < bounds.top || event.clientY > bounds.bottom) this.close();
      });
      this.dialog?.addEventListener('close', () => this.finishClose());

      document.querySelectorAll(`[data-pu-space-viewer-target="${CSS.escape(this.id)}"]`).forEach((trigger) => {
        trigger.addEventListener('pointerdown', () => {
          this.pendingViewport = { x: window.scrollX, y: window.scrollY };
        }, true);
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.open(trigger.dataset.puSpaceViewerProduct, trigger, trigger.dataset.puSpaceViewerMode);
        }, true);
      });
      this.select(this.selectedHandle, true);
    }

    readConfig() {
      try {
        const config = JSON.parse(this.querySelector('[data-pu-space-viewer-config]')?.textContent || '{}');
        return { products: Array.isArray(config.products) ? config.products : [] };
      } catch (_) {
        return { products: [] };
      }
    }

    normalizeImageUrl(value) {
      return typeof value === 'string' && value.startsWith('//') ? `https:${value}` : value;
    }

    isAssetUrl(value) {
      if (typeof value !== 'string') return false;
      try {
        const asset = new URL(value);
        if (asset.protocol === 'https:') return true;
        return asset.protocol === 'http:'
          && ['localhost', '127.0.0.1'].includes(asset.hostname)
          && asset.origin === window.location.origin;
      } catch (_) {
        return false;
      }
    }

    isActive() {
      return this.isConnected && (this.dialog ? this.dialog.open : this.dataset.presentation === 'page');
    }

    isCurrent(handle, token) {
      return this.isActive() && this.selectedHandle === handle && this.renderToken === token;
    }

    open(handle, opener, preferredMode = 'space') {
      if (!this.config.products.length) return;
      this.opener = opener || document.activeElement;
      this.selectedHandle = this.products.has(handle) ? handle : this.selectedHandle;
      if (this.dialog && !this.dialog.open) {
        this.dispatchEvent(new CustomEvent('pu:space-viewer-open', { bubbles: true }));
        this.lockPage(this.pendingViewport);
        this.pendingViewport = null;
        try {
          this.dialog.showModal();
        } catch (_) {
          this.unlockPage();
          return;
        }
      }
      this.mode = preferredMode === '3d' ? '3d' : 'space';
      this.select(this.selectedHandle);
      this.querySelector('[data-pu-space-viewer-close]')?.focus({ preventScroll: true });
    }

    close() {
      if (this.dialog?.open) this.dialog.close();
      this.finishClose();
    }

    finishClose() {
      if (this.dialog?.open) return;
      this.renderToken += 1;
      this.media?.replaceChildren();
      this.media?.setAttribute('aria-busy', 'false');
      this.qrNode?.replaceChildren();
      this.unlockPage();
      this.mode = 'space';
      this.updateModeButtons();
      if (!this.opener) return;
      if (this.opener.isConnected) {
        try { this.opener.focus({ preventScroll: true }); } catch (_) {}
      }
      this.opener = null;
      this.dispatchEvent(new CustomEvent('pu:space-viewer-close', { bubbles: true }));
    }

    disconnectedCallback() {
      this.renderToken += 1;
      this.media?.replaceChildren();
      this.unlockPage();
    }

    handleDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = [...this.dialog.querySelectorAll('button, a[href], model-viewer, [tabindex]')]
        .filter((node) => !node.disabled && node.tabIndex >= 0 && node.getClientRects().length && !node.closest('[hidden]'));
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }

    lockPage(restoreViewport = null) {
      if (this.lockedViewport) return;
      const root = document.documentElement;
      const body = document.body;
      const scrollbar = Math.max(0, window.innerWidth - root.clientWidth);
      this.lockedViewport = {
        x: restoreViewport?.x ?? window.scrollX,
        y: restoreViewport?.y ?? window.scrollY,
        rootOverflow: root.style.overflow,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyRight: body.style.right,
        bodyLeft: body.style.left,
        bodyWidth: body.style.width,
        bodyOverflow: body.style.overflow,
        bodyPaddingRight: body.style.paddingRight
      };
      root.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `${-this.lockedViewport.y}px`;
      body.style.right = '0';
      body.style.left = `${-this.lockedViewport.x}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      if (scrollbar) body.style.paddingRight = `${scrollbar}px`;
    }

    unlockPage() {
      if (!this.lockedViewport) return;
      const viewport = this.lockedViewport;
      const root = document.documentElement;
      const body = document.body;
      root.style.overflow = viewport.rootOverflow;
      body.style.position = viewport.bodyPosition;
      body.style.top = viewport.bodyTop;
      body.style.right = viewport.bodyRight;
      body.style.left = viewport.bodyLeft;
      body.style.width = viewport.bodyWidth;
      body.style.overflow = viewport.bodyOverflow;
      body.style.paddingRight = viewport.bodyPaddingRight;
      this.lockedViewport = null;
      window.scrollTo(viewport.x, viewport.y);
    }

    select(handle, resetMode = false) {
      const product = this.products.get(handle) || this.config.products[0];
      if (!product) return;
      this.selectedHandle = product.handle;
      this.dataset.selectedProduct = product.handle;
      this.querySelectorAll('[data-pu-space-viewer-option]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.puSpaceViewerOption === product.handle));
      });
      if (this.titleNode) this.titleNode.textContent = product.title;
      const scaleNote = this.querySelector('[data-pu-space-viewer-scale-note]');
      if (scaleNote) {
        scaleNote.hidden = !product.scaleNotice;
        scaleNote.textContent = product.scaleNotice || '';
      }
      const handoffCopy = this.handoff?.querySelector('h3 + p');
      if (handoffCopy) handoffCopy.textContent = product.scaleNotice
        ? 'Scan to preview this product in your room. Check the size guidance above.'
        : 'Scan to place this product in your room at its actual size.';
      const arSizeCopy = this.querySelector('.pu-space-viewer__ar-gesture-hint');
      if (arSizeCopy) arSizeCopy.textContent = product.scaleNotice
        ? 'Visual preview. Drag to move and use two fingers to rotate.'
        : 'Actual product size. Drag to move and use two fingers to rotate.';
      if (resetMode) this.mode = 'space';
      this.updateModeButtons();
      if (this.isActive()) {
        this.hydrateThumbnails();
        this.renderCurrentMode();
        const selected = [...this.querySelectorAll('[data-pu-space-viewer-option]')]
          .find((button) => button.dataset.puSpaceViewerOption === product.handle);
        if (selected) this.revealChoice(selected);
      }
    }

    revealModeControl(button) {
      if (!this.dialog?.open) return;
      const header = this.querySelector('.pu-space-viewer__head');
      if (!header) return;
      const headerBounds = header.getBoundingClientRect();
      this.dialog.style.scrollPaddingTop = `${headerBounds.height + 8}px`;
      const hiddenHeight = headerBounds.bottom + 8 - button.getBoundingClientRect().top;
      if (hiddenHeight > 0) this.dialog.scrollTop = Math.max(0, this.dialog.scrollTop - hiddenHeight);
    }

    revealChoice(button) {
      const rail = button.parentElement;
      if (!rail || !this.isActive()) return;
      const itemBounds = button.getBoundingClientRect();
      const railBounds = rail.getBoundingClientRect();
      const railStyle = window.getComputedStyle(rail);
      const start = railBounds.left + (parseFloat(railStyle.scrollPaddingInlineStart) || 0);
      const end = railBounds.right - (parseFloat(railStyle.scrollPaddingInlineEnd) || 0);
      if (itemBounds.left < start) rail.scrollLeft -= start - itemBounds.left;
      else if (itemBounds.right > end) rail.scrollLeft += itemBounds.right - end;
    }

    hydrateThumbnails() {
      if (!this.isActive()) return;
      this.querySelectorAll('[data-pu-space-viewer-option]').forEach((button) => {
        const thumbnail = button.querySelector('[data-pu-space-viewer-thumb]');
        const product = this.products.get(button.dataset.puSpaceViewerOption);
        if (!thumbnail || !product || thumbnail.childElementCount) return;
        const icon = () => {
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.setAttribute('viewBox', '0 0 24 24');
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');
          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', 'm12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Zm-8 4.5 8 4.5 8-4.5M12 12v9');
          svg.appendChild(path);
          thumbnail.replaceChildren(svg);
        };
        const source = product.poster || product.hero;
        if (!this.isAssetUrl(source)) { icon(); return; }
        const image = document.createElement('img');
        image.alt = '';
        image.width = 72;
        image.height = 72;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.addEventListener('error', icon, { once: true });
        image.src = source;
        thumbnail.appendChild(image);
      });
    }

    setMode(mode) {
      if (mode !== 'space' && mode !== '3d') return;
      if (this.mode === mode) return;
      this.mode = mode;
      this.updateModeButtons();
      this.renderCurrentMode();
    }

    updateModeButtons() {
      this.dataset.mode = this.mode;
      this.querySelectorAll('[data-pu-space-viewer-mode]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.puSpaceViewerMode === this.mode));
      });
    }

    renderCurrentMode() {
      if (!this.isActive()) return;
      const product = this.products.get(this.selectedHandle);
      if (!product) return;
      const token = ++this.renderToken;
      this.hideStatus();
      this.handoff.hidden = this.mobileDevice || this.mode !== 'space';
      this.spaceAction.hidden = !this.mobileDevice || this.mode !== 'space';
      this.gestureHint.hidden = true;
      this.media.setAttribute('aria-busy', 'false');
      if (this.mode === '3d' || !this.renderHero(product, token)) this.loadSelectedModel(product, token);
      if (!this.handoff.hidden) this.renderQr(product, token);
    }

    renderHero(product, token) {
      const source = product.hero || product.poster;
      if (!this.media || !this.isAssetUrl(source) || !this.isCurrent(product.handle, token)) return false;
      const figure = document.createElement('figure');
      figure.className = 'pu-space-viewer__hero';
      const image = document.createElement('img');
      image.src = source;
      image.alt = product.heroAlt || product.title;
      image.decoding = 'async';
      image.loading = 'eager';
      image.addEventListener('error', () => {
        if (this.isCurrent(product.handle, token)) this.loadSelectedModel(product, token);
      }, { once: true });
      figure.appendChild(image);
      this.media.replaceChildren(figure);
      return true;
    }

    showStatus(message, retry = false) {
      if (!this.status || !this.statusRow) return;
      this.status.textContent = message;
      this.statusRow.hidden = false;
      if (this.retryButton) this.retryButton.hidden = !retry;
    }

    hideStatus() {
      if (this.statusRow) this.statusRow.hidden = true;
      if (this.status) this.status.textContent = '';
      if (this.retryButton) this.retryButton.hidden = true;
    }

    openCamera() {
      const product = this.products.get(this.selectedHandle);
      if (!product || !this.isActive()) return;
      if (!this.mobileDevice) {
        this.setMode('space');
        return;
      }
      const iosDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      try {
        if (iosDevice) {
          const quickLookLink = document.createElement('a');
          quickLookLink.rel = 'ar';
          const quickLookUrl = new URL(product.usdz, window.location.href);
          quickLookUrl.hash = 'allowsContentScaling=0';
          quickLookLink.href = quickLookUrl.toString();
          quickLookLink.setAttribute('aria-hidden', 'true');
          quickLookLink.tabIndex = -1;
          const preview = document.createElement('img');
          preview.alt = '';
          preview.src = product.poster || product.hero
            || 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
          preview.width = 1;
          preview.height = 1;
          quickLookLink.appendChild(preview);
          Object.assign(quickLookLink.style, { position: 'fixed', left: '-9999px', top: '0', opacity: '0' });
          document.body.appendChild(quickLookLink);
          quickLookLink.click();
          window.setTimeout(() => quickLookLink.remove(), 2000);
        } else {
          const sceneViewerUrl = new URL('https://arvr.google.com/scene-viewer/1.0');
          sceneViewerUrl.searchParams.set('file', product.glb);
          sceneViewerUrl.searchParams.set('mode', 'ar_only');
          sceneViewerUrl.searchParams.set('resizable', 'false');
          window.location.assign(sceneViewerUrl.toString());
        }
      } catch (_) {
        this.showStatus('The camera viewer could not open. Try Open camera again, or use 3D view.', false);
      }
    }

    isMobileDevice() {
      const touch = navigator.maxTouchPoints > 0;
      const coarse = window.matchMedia?.('(pointer: coarse)').matches;
      const narrow = window.matchMedia?.('(max-width: 767px)').matches;
      return Boolean((touch && coarse) || narrow);
    }

    handoffUrl(product) {
      const target = new URL(product?.url || window.location.pathname, window.location.origin);
      target.searchParams.set('view', 'space-viewer');
      target.searchParams.set('space_product', product.handle);
      target.searchParams.delete('preview_theme_id');
      const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id') || this.dataset.unpublishedThemeId;
      if (previewThemeId) target.searchParams.set('preview_theme_id', previewThemeId);
      return target.toString();
    }

    retryCurrentView() {
      if (!this.isActive()) return;
      const product = this.products.get(this.selectedHandle);
      if (!product) return;
      const restoreFocus = document.activeElement === this.retryButton;
      if (this.failedModelSources.has(product.glb)) {
        const retryUrl = new URL(product.glb);
        this.modelRetryAttempt += 1;
        retryUrl.searchParams.set('pu_model_retry', `${Date.now()}-${this.modelRetryAttempt}`);
        this.modelSourceOverrides.set(product.glb, retryUrl.toString());
        this.failedModelSources.delete(product.glb);
      }
      this.renderCurrentMode();
      if (restoreFocus) {
        const modeButton = [...this.querySelectorAll('[data-pu-space-viewer-mode]')]
          .find((button) => button.dataset.puSpaceViewerMode === this.mode);
        modeButton?.focus({ preventScroll: true });
      }
    }

    loadSelectedModel(product, token) {
      if (!product?.glb || !this.media || !this.isCurrent(product.handle, token)) return;
      const loading = document.createElement('p');
      loading.className = 'pu-space-viewer__loading';
      loading.textContent = 'Loading 3D view…';
      this.media.replaceChildren(loading);
      this.media.setAttribute('aria-busy', 'true');
      const failed = (modelFailure = false) => {
        if (!this.isCurrent(product.handle, token)) return;
        if (modelFailure) this.failedModelSources.add(product.glb);
        this.media.setAttribute('aria-busy', 'false');
        this.media.replaceChildren();
        this.gestureHint.hidden = true;
        this.showStatus('The 3D view could not load. Please try again.', true);
      };
      this.loadScript('model-viewer', this.dataset.modelViewerScriptUrl, () => customElements.get('model-viewer'))
        .then(() => {
          if (!this.isCurrent(product.handle, token)) return;
          const model = document.createElement('model-viewer');
          model.setAttribute('src', this.modelSourceOverrides.get(product.glb) || product.glb);
          model.setAttribute('alt', `Interactive 3D view of ${product.title}`);
          model.setAttribute('camera-controls', '');
          if (product.cameraOrbit) model.setAttribute('camera-orbit', product.cameraOrbit);
          model.setAttribute('touch-action', 'pan-y');
          model.setAttribute('interaction-prompt', 'auto');
          model.setAttribute('shadow-intensity', '0.5');
          model.setAttribute('ar-scale', 'fixed');
          if (this.isAssetUrl(product.poster)) model.setAttribute('poster', product.poster);
          model.setAttribute('ios-src', product.usdz);
          model.addEventListener('load', () => {
            if (!this.isCurrent(product.handle, token)) return;
            this.media.setAttribute('aria-busy', 'false');
            this.gestureHint.hidden = false;
          }, { once: true });
          model.addEventListener('error', () => failed(true), { once: true });
          this.media.replaceChildren(model);
        }).catch(() => failed());
    }

    loadScript(name, url, ready) {
      if (ready()) return Promise.resolve();
      if (scriptPromises.has(name)) return scriptPromises.get(name);
      const promise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        let timeout;
        const fail = () => {
          window.clearTimeout(timeout);
          script.remove();
          reject(new Error(`${name} could not load`));
        };
        script.src = url;
        if (name === 'model-viewer') script.type = 'module';
        script.onload = () => {
          window.clearTimeout(timeout);
          if (ready()) resolve(); else fail();
        };
        script.onerror = fail;
        timeout = window.setTimeout(fail, 20000);
        document.head.appendChild(script);
      }).catch((error) => {
        scriptPromises.delete(name);
        throw error;
      });
      scriptPromises.set(name, promise);
      return promise;
    }

    renderQr(product, token) {
      if (!this.qrNode || !this.isCurrent(product.handle, token)) return;
      const url = this.handoffUrl(product);
      this.handoffLink.href = url;
      this.qrNode.replaceChildren();
      this.qrStatus.textContent = 'Loading QR code…';
      this.qrStatus.hidden = false;
      this.qrRetry.hidden = true;
      this.loadScript('qrcode', this.dataset.qrScriptUrl, () => window.QRCode).then(() => {
        if (!this.isCurrent(product.handle, token) || this.handoff.hidden) return;
        new window.QRCode(this.qrNode, {
          text: url,
          width: 144,
          height: 144,
          colorDark: '#1d1d1f',
          colorLight: '#ffffff',
          correctLevel: window.QRCode.CorrectLevel.M
        });
        const qrImage = this.qrNode.querySelector('img');
        if (qrImage) qrImage.alt = '';
        this.qrNode.setAttribute('aria-label', `QR code to view ${product.title} on your phone`);
        this.qrNode.querySelector('canvas')?.setAttribute('aria-hidden', 'true');
        this.qrStatus.hidden = true;
      }).catch(() => {
        if (!this.isCurrent(product.handle, token) || this.handoff.hidden) return;
        this.qrStatus.textContent = 'The QR code could not load. Try again or open the phone viewer link.';
        this.qrStatus.hidden = false;
        this.qrRetry.hidden = false;
      });
    }
  }

  customElements.define('pu-space-viewer', ProjectUnitySpaceViewer);
})();
